import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Form, Modal, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FaEdit } from 'react-icons/fa';
import adminSchemaApi from '../api/adminSchemaApi';
import '../styles/admin-theme.css';

const INITIAL_FORM = {
  name: '',
  tenantcode: '',
  isactive: true,
  systememail: '',
  adminemail: '',
};

const AdminSchemas = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState('');

  const loadCompanies = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await adminSchemaApi.fetchSchemas();
      setCompanies(Array.isArray(data) ? data : []);
    } catch (err) {
      const status = err?.status ? ` (status ${err.status})` : '';
      setError(`Failed to load schemas${status}.`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  const openCreate = () => {
    setEditing(null);
    setForm(INITIAL_FORM);
    setModalError('');
    setShowModal(true);
  };

  const openEdit = (row) => {
    const id = row.company_id || row.id;
    if (!id) return;
    setEditing(row);
    setForm({
      name: row.name ?? '',
      tenantcode: row.tenantcode ?? '',
      isactive: row.isactive !== false,
      systememail: row.systememail ?? '',
      adminemail: row.adminemail ?? '',
    });
    setModalError('');
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditing(null);
    setForm(INITIAL_FORM);
    setModalError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const name = (form.name || '').trim();
    const tenantcode = (form.tenantcode || '').trim();
    if (!name || !tenantcode) {
      setModalError('Name and tenant code are required.');
      return;
    }
    setSaving(true);
    setModalError('');
    try {
      const payload = {
        name,
        tenantcode,
        isactive: !!form.isactive,
        systememail: (form.systememail || '').trim() || undefined,
        adminemail: (form.adminemail || '').trim() || undefined,
      };
      if (editing) {
        const id = editing.company_id || editing.id;
        await adminSchemaApi.updateCompany(id, payload);
      } else {
        await adminSchemaApi.createCompany(payload);
      }
      handleCloseModal();
      await loadCompanies();
    } catch (err) {
      setModalError(err?.message || 'Failed to save company');
    } finally {
      setSaving(false);
    }
  };

  const searchValue = (search || '').trim().toLowerCase();
  const filteredCompanies = !searchValue
    ? companies
    : companies.filter((c) => {
        const name = String(c?.name ?? '').toLowerCase();
        const tenantcode = String(c?.tenantcode ?? '').toLowerCase();
        return name.includes(searchValue) || tenantcode.includes(searchValue);
      });

  return (
    <>
      <h2 className='admin-title'>Schemas</h2>
      <p className='admin-subtitle'>Companies list mapped to schemas.</p>

      {error && <Alert variant='danger'>{error}</Alert>}

      <div className='d-flex justify-content-between align-items-center gap-2 mb-3 flex-wrap'>
        <div style={{ minWidth: 260, maxWidth: 420, flex: '1 1 320px' }}>
          <Form.Control
            type='text'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder='Search company or schema...'
            aria-label='Search company or schema'
          />
        </div>
        <Button className='admin-primary-btn' onClick={openCreate}>
          Create company
        </Button>
      </div>

      {loading ? (
        <div className='admin-subtitle'>Loading...</div>
      ) : (
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Company</th>
              <th>Schema</th>
              <th>Active</th>
              <th>System Email</th>
              <th>Admin Email</th>
              <th>Config</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredCompanies.map(company => {
              const companyId = company.company_id || company.id;
              return (
                <tr key={companyId || company.tenantcode || company.name}>
                  <td>{company.name || '-'}</td>
                  <td>{company.tenantcode || '-'}</td>
                  <td>{company.isactive ? 'Yes' : 'No'}</td>
                  <td>{company.systememail || '-'}</td>
                  <td>{company.adminemail || '-'}</td>
                  <td>
                    {company.tenantcode ? (
                      <Link to={`/admin/schemas/${company.tenantcode}`}>
                        Configure
                      </Link>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td>
                    {companyId ? (
                      <>
                        <Button
                          variant='link'
                          className='p-0 me-2'
                          size='sm'
                          onClick={() => openEdit(company)}
                          title='Edit company'
                        >
                          <FaEdit />
                        </Button>
                        <Link to={`/admin/company/${companyId}`}>View</Link>
                      </>
                    ) : (
                      '-'
                    )}
                  </td>
                </tr>
              );
            })}
            {filteredCompanies.length === 0 && (
              <tr>
                <td colSpan={7} className='text-center'>
                  No companies found.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      )}

      <Modal show={showModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton>
          <Modal.Title className='admin-title'>
            {editing ? 'Edit company' : 'Create company'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {modalError && (
              <Alert variant='danger' dismissible onClose={() => setModalError('')} className='mb-3'>
                {modalError}
              </Alert>
            )}
            <Form.Group className='mb-2'>
              <Form.Label>Name</Form.Label>
              <Form.Control
                type='text'
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder='Company name'
                required
              />
            </Form.Group>
            <Form.Group className='mb-2'>
              <Form.Label>Tenant code (schema)</Form.Label>
              <Form.Control
                type='text'
                value={form.tenantcode}
                onChange={(e) => setForm((f) => ({ ...f, tenantcode: e.target.value }))}
                placeholder='e.g. acme'
                required
                disabled={!!editing}
              />
              {editing && (
                <Form.Text className='text-muted'>Tenant code cannot be changed after creation.</Form.Text>
              )}
            </Form.Group>
            <Form.Group className='mb-2'>
              <Form.Check
                type='switch'
                id='company-isactive'
                label='Active'
                checked={!!form.isactive}
                onChange={(e) => setForm((f) => ({ ...f, isactive: e.target.checked }))}
              />
            </Form.Group>
            <Form.Group className='mb-2'>
              <Form.Label>System email</Form.Label>
              <Form.Control
                type='email'
                value={form.systememail}
                onChange={(e) => setForm((f) => ({ ...f, systememail: e.target.value }))}
                placeholder='system@company.com'
              />
            </Form.Group>
            <Form.Group className='mb-2'>
              <Form.Label>Admin email</Form.Label>
              <Form.Control
                type='email'
                value={form.adminemail}
                onChange={(e) => setForm((f) => ({ ...f, adminemail: e.target.value }))}
                placeholder='admin@company.com'
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant='outline-secondary' onClick={handleCloseModal} disabled={saving}>
              Cancel
            </Button>
            <Button type='submit' className='admin-primary-btn' disabled={saving}>
              {saving ? 'Saving...' : editing ? 'Update' : 'Create'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default AdminSchemas;
